import React, { useState } from 'react';
import { useQuery, gql } from "@apollo/client";

const GET_EMPLOYEE_TIMELINE = gql`
  query GetEmployeeTimeline($employeeId: ID, $employeeIds: [ID], $date: String) {
    getEmployeeTimeline(employeeId: $employeeId, employeeIds: $employeeIds, date: $date) {
      status
      message
      data {
        employeeId
        fullName
        date
        timeline {
          startTime
          endTime
          type
          durationMins
          bookingId
          reason
          status
        }
      }
    }
  }
`;


const EmployeeTimelineDashboard = ({
  // Props passed from AdminDashboard (mirrors getTimeSeriesStat filter)
  employees = [],
  employeeIds: externalEmployeeIds,
  employeeId: externalEmployeeId,
}) => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  // Local employee multi-select — used when no external filter is active
  const [localEmployeeIds, setLocalEmployeeIds] = useState([]);

  // Resolve effective filters: external props take precedence
  const effectiveEmployeeIds =
    externalEmployeeIds && externalEmployeeIds.length > 0
      ? externalEmployeeIds
      : localEmployeeIds;
  const effectiveEmployeeId =
    !effectiveEmployeeIds.length && externalEmployeeId
      ? externalEmployeeId
      : undefined;

  const { data, loading } = useQuery(GET_EMPLOYEE_TIMELINE, {
    variables: {
      date,
      employeeIds: effectiveEmployeeIds.length > 0 ? effectiveEmployeeIds : undefined,
      employeeId: effectiveEmployeeId,
    },
    fetchPolicy: "network-only"
  });

  const timelineData = data?.getEmployeeTimeline?.status === 'Success'
    ? data.getEmployeeTimeline.data
    : [];

  // Toggle employee in local multi-select
  const toggleLocalEmployee = (id) => {
    setLocalEmployeeIds((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]
    );
  };

  // Utility to convert HH:MM to minutes from midnight for plotting
  const getMinutes = (timeString) => {
    if (!timeString) return 0;
    const [h, m] = timeString.split(':').map(Number);
    return h * 60 + m;
  };

  const dayStartMins = 8 * 60; // 08:00 AM
  const dayEndMins = 20 * 60;  // 08:00 PM
  const totalDayMins = dayEndMins - dayStartMins;

  // Render a single slot block
  const renderSlot = (slot, index) => {
    const startMins = getMinutes(slot.startTime);
    const endMins = getMinutes(slot.endTime);

    // Boundary checks for the UI view (8am to 8pm)
    const renderStart = Math.max(startMins, dayStartMins);
    const renderEnd = Math.min(endMins, dayEndMins);
    const renderDuration = renderEnd - renderStart;

    if (renderDuration <= 0) return null; // Outside our viewing window

    const widthPercent = (renderDuration / totalDayMins) * 100;
    const leftPercent = ((renderStart - dayStartMins) / totalDayMins) * 100;

    let bgColor = '#e2e8f0'; // Gray for FREE
    let textColor = '#475569';
    if (slot.type === 'BOOKED') {
      bgColor = '#3b82f6'; // Blue for BOOKED
      textColor = '#ffffff';
    } else if (slot.type === 'BLOCKED') {
      bgColor = '#ef4444'; // Red for BLOCKED
      textColor = '#ffffff';
    }

    return (
      <div
        key={index}
        style={{
          position: 'absolute',
          left: `${leftPercent}%`,
          width: `${widthPercent}%`,
          backgroundColor: bgColor,
          color: textColor,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.75rem',
          fontWeight: 'bold',
          borderRight: '1px solid white',
          boxSizing: 'border-box',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          padding: '0 4px',
          cursor: slot.type !== 'FREE' ? 'pointer' : 'default',
        }}
        title={`${slot.type} (${slot.startTime} - ${slot.endTime}) ${slot.bookingId ? 'ID: ' + slot.bookingId : ''} ${slot.reason ? 'Reason: ' + slot.reason : ''}`}
      >
        {renderDuration > 30 ? slot.type : ''}
      </div>
    );
  };

  const hasExternalFilter = (externalEmployeeIds && externalEmployeeIds.length > 0) || !!externalEmployeeId;

  return (
    <div style={{ padding: '20px', fontFamily: 'sans-serif', color: '#f8fafc' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ color: '#FBB033', fontSize: '1.25rem', fontWeight: 'bold' }}>Employee Slots Timeline</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>

          {/* Indicate if external filter from dashboard is applied */}
          {hasExternalFilter && (
            <span style={{ fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic' }}>
              Filtered by dashboard employee selection
            </span>
          )}

          <div>
            <label style={{ marginRight: '10px', fontWeight: 'bold', color: '#cbd5e1' }}>Select Date:</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #334155', backgroundColor: '#0f0f0f', color: '#f8fafc' }}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <p>Loading timeline data...</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', minWidth: '700px' }}>

            {/* Timeline Header (Hours) */}
            <div style={{ display: 'flex', marginLeft: '150px', position: 'relative', height: '20px' }}>
              {[8, 10, 12, 14, 16, 18, 20].map((hour) => {
                const leftPercent = ((hour * 60 - dayStartMins) / totalDayMins) * 100;
                return (
                  <div key={hour} style={{ position: 'absolute', left: `${leftPercent}%`, transform: 'translateX(-50%)', fontSize: '0.8rem', color: '#64748b' }}>
                    {hour > 12 ? `${hour - 12} PM` : hour === 12 ? '12 PM' : `${hour} AM`}
                  </div>
                );
              })}
            </div>

            {/* Timeline Rows */}
            {timelineData.map((emp) => (
              <div key={emp.employeeId} style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div style={{ width: '135px', fontWeight: 'bold', fontSize: '0.9rem', color: '#e2e8f0', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }} title={emp.fullName}>
                  {emp.fullName}
                </div>

                <div style={{ flex: 1, height: '40px', backgroundColor: '#1e293b', position: 'relative', borderRadius: '6px', overflow: 'hidden', border: '1px solid #334155' }}>
                  {/* Background grid lines */}
                  {[8, 10, 12, 14, 16, 18, 20].map((hour) => {
                    const leftPercent = ((hour * 60 - dayStartMins) / totalDayMins) * 100;
                    return (
                      <div key={`grid-${hour}`} style={{ position: 'absolute', left: `${leftPercent}%`, width: '1px', height: '100%', backgroundColor: '#334155', zIndex: 0 }} />
                    );
                  })}

                  {/* Slots */}
                  <div style={{ position: 'absolute', width: '100%', height: '100%', zIndex: 1 }}>
                    {emp.timeline.map((slot, idx) => renderSlot(slot, idx))}
                  </div>
                </div>
              </div>
            ))}

            {timelineData.length === 0 && !loading && (
              <div style={{ textAlign: 'center', color: '#94a3b8', marginTop: '40px' }}>
                No active employees found for this date.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginTop: '30px', padding: '15px', backgroundColor: '#0f0f0f', borderRadius: '8px', border: '1px solid #334155' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '16px', height: '16px', backgroundColor: '#3b82f6', borderRadius: '4px' }}></div>
          <span style={{ fontSize: '0.9rem', color: '#cbd5e1' }}>Booked Booking</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '16px', height: '16px', backgroundColor: '#ef4444', borderRadius: '4px' }}></div>
          <span style={{ fontSize: '0.9rem', color: '#cbd5e1' }}>Admin/Employee Blocked</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '16px', height: '16px', backgroundColor: '#e2e8f0', borderRadius: '4px', border: '1px solid #94a3b8' }}></div>
          <span style={{ fontSize: '0.9rem', color: '#cbd5e1' }}>Free / Available</span>
        </div>
      </div>

    </div>
  );
};

export default EmployeeTimelineDashboard;
